import { useState, useEffect } from "react";
import Pagination from "../common/Pagination";
import { apiGet, apiPost } from "../../utils/apiClient";
import { buildPayoutFilterParams } from "./payoutFilterParams";

const PayoutDateList = ({ storeCode, storeName, filterMode, filters, isCompleted, activeTab, pgStatus, onDateSelect, onTabChange, onTotalsChange }) => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkBarBottom, setBulkBarBottom] = useState(40);
  const [modalInfo, setModalInfo] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);

  useEffect(() => {
    const updateBarBottom = () => {
      const footer = document.querySelector(".admin-site-footer");
      if (!footer) return;
      const footerTop = footer.getBoundingClientRect().top;
      const viewportHeight = window.innerHeight;
      setBulkBarBottom(footerTop < viewportHeight ? viewportHeight - footerTop + 16 : 40);
    };
    window.addEventListener("scroll", updateBarBottom);
    updateBarBottom();
    return () => window.removeEventListener("scroll", updateBarBottom);
  }, []);

  // 모달이 떠 있는 동안 배경 스크롤 방지
  useEffect(() => {
    if (!modalInfo && !confirmVisible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [modalInfo, confirmVisible]);

  const getItemId = (item) => item.payoutPeriodDate;

  // 정산 예정 화면의 PG 정산 예정 탭에서는 정산 처리를 할 수 없으므로 체크박스를 숨김
  const showCheckbox = isCompleted || activeTab !== "pending";

  // 정산 가능 금액(PG 미정산 제외) — 0원 이하이면 선택 불가
  const isSettleable = (d) => ((d.payoutFinal ?? 0) - (d.pendingFinal ?? 0)) > 0;
  const settleableDates = dates.filter(isSettleable);

  const selectedDates = dates.filter((d) => selectedItems.includes(getItemId(d)));
  const selectedTotals = {
    sales: selectedDates.reduce((sum, d) => sum + (d.payoutPrice ?? 0), 0),
    pgFee: selectedDates.reduce((sum, d) => sum + (d.payoutBaseTax ?? 0), 0),
    cpFee: selectedDates.reduce((sum, d) => sum + (d.payoutCustomTax ?? 0), 0),
    payout: selectedDates.reduce((sum, d) => sum + (d.payoutFinal ?? 0), 0),
  };

  // 정산 모달 breakdown: 정산 가능액(전체) − 정산액(선택) = 정산 잔액
  const settlePayout = (d) => (d.payoutFinal ?? 0) - (d.pendingFinal ?? 0);
  const totalPayout = dates.reduce((sum, d) => sum + settlePayout(d), 0);
  const modalPayout = (modalInfo?.dates ?? []).reduce((sum, d) => sum + settlePayout(d), 0);

  useEffect(() => {
    onTotalsChange({
      sales: dates.reduce((sum, d) => sum + (d.payoutPrice ?? 0), 0),
      pgFee: dates.reduce((sum, d) => sum + (d.payoutBaseTax ?? 0), 0),
      cpFee: dates.reduce((sum, d) => sum + (d.payoutCustomTax ?? 0), 0),
      payout: dates.reduce((sum, d) => sum + (d.payoutFinal ?? 0), 0),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates]);

  const handleSelectAll = (e) => {
    setSelectedItems(e.target.checked ? settleableDates.map((d) => getItemId(d)) : []);
  };

  const handleSelectOne = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    loadDates();
    setSelectedItems([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pgStatus]);

  const loadDates = async (page = 0) => {
    setLoading(true);
    setError(null);
    try {
      const params = buildPayoutFilterParams(filterMode, filters);
      params.append("storeCode", storeCode);
      params.append("page", page);
      params.append("size", 10);
      params.append("pgCompleted", pgStatus);
      const endpoint = isCompleted ? "/admin/payout/complete/time" : "/admin/payout/pending/time";
      const response = await apiGet(`${endpoint}?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        // 응답이 Page 형태(content 포함)이면 페이지 정보 사용, List 형태이면 전체 사용
        if (Array.isArray(data)) {
          setDates(data);
          setCurrentPage(0);
          setTotalPages(1);
        } else {
          setDates(data.content || []);
          setCurrentPage(data.number || 0);
          setTotalPages(data.totalPages || 0);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || "날짜 목록 조회에 실패했습니다.");
        setDates([]);
      }
    } catch (err) {
      setError(err.message || "날짜 목록 조회에 실패했습니다.");
      console.error("날짜 목록 조회 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadDates(page);
    setSelectedItems([]);
  };

  // 정산 취소 — 1차 모달 표시
  const handleBulkCancel = () => {
    if (selectedItems.length === 0) return;
    const cancelledDates = dates
      .filter((d) => selectedItems.includes(getItemId(d)))
      .map((d) => ({ payoutPeriodDate: d.payoutPeriodDate, payoutFinal: d.payoutFinal }));
    // /admin/payout/revert/detail 은 storeCode + payoutPeriodDates(리스트)를 한 번에 받음
    const body = { storeCode, payoutPeriodDates: selectedItems };
    setModalInfo({ dates: cancelledDates, isCancel: true, body });
  };

  // 일괄정산 — 1차 모달 표시
  const handleBulkSettle = () => {
    if (selectedItems.length === 0) return;
    const settledDates = dates
      .filter((d) => selectedItems.includes(getItemId(d)))
      .map((d) => ({
        payoutPeriodDate: d.payoutPeriodDate,
        payoutFinal: d.payoutFinal ?? 0,
        pendingFinal: d.pendingFinal ?? 0,
      }));
    // /admin/payout/settle/detail 은 storeCode + payoutPeriodDates(리스트)를 한 번에 받음
    const body = { storeCode, payoutPeriodDates: selectedItems };
    setModalInfo({ dates: settledDates, isCancel: false, body });
  };

  // 2차 확인 후 실제 API 호출
  const handleConfirmAction = async () => {
    if (modalInfo.isCancel) {
      try {
        const response = await apiPost("/admin/payout/revert/detail", modalInfo.body);
        if (response.ok) {
          setSelectedItems([]);
          loadDates(currentPage);
          setModalInfo(null);
          setConfirmVisible(false);
        } else {
          const errorData = await response.json().catch(() => ({}));
          alert(errorData.message || "정산 취소 처리에 실패했습니다.");
        }
      } catch (err) {
        console.error("정산 취소 처리 실패:", err);
        alert("정산 취소 처리에 실패했습니다.");
      }
      return;
    }

    try {
      const response = await apiPost("/admin/payout/settle/detail", modalInfo.body);
      if (response.ok) {
        setSelectedItems([]);
        loadDates(currentPage);
        setModalInfo(null);
        setConfirmVisible(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || "일괄정산 처리에 실패했습니다.");
      }
    } catch (err) {
      console.error("처리 실패:", err);
      alert("일괄정산 처리에 실패했습니다.");
    }
  };

  return (
    <div className="adminpayoutstore-list" style={selectedItems.length > 0 ? { paddingBottom: 100 } : undefined}>
      <div className="adminpayoutstore-tab-navigation">
        <button
          className={`adminpayoutstore-tab-btn ${activeTab === "completed" ? "adminpayoutstore-tab-active" : ""}`}
          onClick={() => onTabChange("completed")}
        >
          PG 정산 완료
        </button>
        {!isCompleted && (
          <button
            className={`adminpayoutstore-tab-btn ${activeTab === "pending" ? "adminpayoutstore-tab-active" : ""}`}
            onClick={() => onTabChange("pending")}
          >
            PG 정산 예정
          </button>
        )}
      </div>
      {loading ? (
        <div className="adminpayoutstore-loading">로딩 중</div>
      ) : error ? (
        <div className="adminpayoutstore-error">{error}</div>
      ) : (
        <table className="adminpayoutstore-table">
          <thead>
            <tr>
              {showCheckbox && (
                <th className="adminpayoutstore-th-checkbox">
                  <input
                    type="checkbox"
                    checked={settleableDates.length > 0 && selectedItems.length === settleableDates.length}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              <th>결제일</th>
              <th>PG 정산일</th>
              <th>총 매출액</th>
              <th>PG 수수료</th>
              <th>자사 수수료</th>
              <th>실 정산액</th>
              <th>기타</th>
            </tr>
          </thead>
          <tbody>
            {dates.length === 0 ? (
              <tr>
                <td colSpan={showCheckbox ? 8 : 7} style={{ textAlign: "center", fontWeight: "bold" }}>
                  데이터가 없습니다.
                </td>
              </tr>
            ) : (
              dates.map((item) => (
                <tr
                  key={getItemId(item)}
                  className={selectedItems.includes(getItemId(item)) ? "adminpayoutstore-row-selected" : ""}
                >
                  {showCheckbox && (
                    <td className="adminpayoutstore-td-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(getItemId(item))}
                        disabled={!isSettleable(item)}
                        title={!isSettleable(item) ? "정산 가능한 금액이 없습니다." : undefined}
                        onChange={() => handleSelectOne(getItemId(item))}
                      />
                    </td>
                  )}
                  <td>{item.payoutPeriodDate}</td>
                  <td>{item.payoutScheduledDate}</td>
                  <td>{item.payoutPrice?.toLocaleString()}원</td>
                  <td>{item.payoutBaseTax?.toLocaleString()}원</td>
                  <td>{item.payoutCustomTax?.toLocaleString()}원</td>
                  <td>{item.payoutFinal?.toLocaleString()}원</td>
                  <td>
                    <button
                      className="adminpayoutstore-btn-detail"
                      onClick={() =>
                        onDateSelect(item.payoutPeriodDate, {
                          sales: item.payoutPrice,
                          fee: item.payoutBaseTax + item.payoutCustomTax,
                          payout: item.payoutFinal,
                        })
                      }
                    >
                      상세
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      {modalInfo && (
        <div className="adminpayoutstore-modal-overlay">
          <div className="adminpayoutstore-modal">
            <div className="adminpayoutstore-modal-header">
              <span className="adminpayoutstore-modal-title">{modalInfo?.isCancel ? "정산 취소" : "정산"}</span>
              <button className="adminpayoutstore-modal-close" onClick={() => setModalInfo(null)}>✕</button>
            </div>
            <div className="adminpayoutstore-modal-store-info">
              <span className="adminpayoutstore-modal-storecode">{storeCode}</span>
              <span className="adminpayoutstore-modal-storename">{storeName}</span>
              <span className="adminpayoutstore-modal-count">총 {modalInfo.dates.length}건</span>
            </div>
            <div className="adminpayoutstore-modal-table-wrap">
              <table className="adminpayoutstore-modal-table">
                <thead>
                  <tr>
                    <th>결제일</th>
                    <th>실 정산액</th>
                  </tr>
                </thead>
                <tbody>
                  {modalInfo.dates.map((d, i) => (
                    <tr key={d.payoutPeriodDate ?? i}>
                      <td>{d.payoutPeriodDate}</td>
                      <td>{((d.payoutFinal ?? 0) - (d.pendingFinal ?? 0)).toLocaleString()}원</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!modalInfo.isCancel && (
              <div className="adminpayoutstore-modal-breakdown">
                <div className="adminpayoutstore-modal-breakdown-item">
                  <div className="adminpayoutstore-info-title">정산 가능액</div>
                  <div className="adminpayoutstore-info-amount">{totalPayout.toLocaleString()}원</div>
                </div>
                <div className="adminpayoutstore-ver-divider" />
                <div className="adminpayoutstore-modal-breakdown-item">
                  <div className="adminpayoutstore-info-title">정산액</div>
                  <div className="adminpayoutstore-info-amount">{modalPayout.toLocaleString()}원</div>
                </div>
                <div className="adminpayoutstore-ver-divider" />
                <div className="adminpayoutstore-modal-breakdown-item adminpayoutstore-modal-breakdown-remain">
                  <div className="adminpayoutstore-info-title">정산 잔액</div>
                  <div className="adminpayoutstore-info-amount">{(totalPayout - modalPayout).toLocaleString()}원</div>
                </div>
              </div>
            )}
            <div className="adminpayoutstore-modal-footer">
              <button className="adminpayoutstore-modal-confirm" onClick={() => setConfirmVisible(true)}>
                확인
              </button>
              <button className="adminpayoutstore-modal-cancel-btn" onClick={() => setModalInfo(null)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmVisible && modalInfo && (
        <div className="adminpayoutstore-modal-overlay">
          <div className="adminpayoutstore-modal adminpayoutstore-modal-confirm-dialog">
            <div className="adminpayoutstore-modal-header">
              <span className="adminpayoutstore-modal-title">최종 확인</span>
              <button className="adminpayoutstore-modal-close" onClick={() => setConfirmVisible(false)}>✕</button>
            </div>
            <p className="adminpayoutstore-modal-confirm-text">
              선택한 {modalInfo.dates.length}건을&nbsp;
              {modalInfo.isCancel ? "정산 취소" : "정산"} 처리합니다.<br />진행하시겠습니까?
            </p>
            <div className="adminpayoutstore-modal-footer">
              <button className="adminpayoutstore-modal-confirm" onClick={handleConfirmAction}>
                확인
              </button>
              <button className="adminpayoutstore-modal-cancel-btn" onClick={() => setConfirmVisible(false)}>
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedItems.length > 0 && (
        <div className="adminpayoutstore-bulk-bar" style={{ bottom: bulkBarBottom }}>
          <div className="adminpayoutstore-bulk-info">
            <div className="adminpayoutstore-bulk-count-row">
              <span className="adminpayoutstore-bulk-count">
                {selectedItems.length}개 선택됨
              </span>
              {!isCompleted && (
                <span className="adminpayoutstore-bulk-notice">
                  PG 미정산 건은 제외하고 정산됩니다.
                </span>
              )}
            </div>
            <div className="adminpayoutstore-bulk-totals">
              <span>총 매출액<b>{selectedTotals.sales.toLocaleString()}원</b></span>
              <span>PG 수수료<b>{selectedTotals.pgFee.toLocaleString()}원</b></span>
              <span>자사 수수료<b>{selectedTotals.cpFee.toLocaleString()}원</b></span>
              <span>실 정산액<b>{selectedTotals.payout.toLocaleString()}원</b></span>
            </div>
          </div>
          <div className="adminpayoutstore-bulk-bar-actions">
            <button
              className="adminpayoutstore-btn-bulk"
              onClick={isCompleted ? handleBulkCancel : handleBulkSettle}
            >
              {isCompleted ? "일괄 정산 취소" : "일괄정산"}
            </button>
            <button className="adminpayoutstore-btn-bulk-cancel" onClick={() => setSelectedItems([])}>
              취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutDateList;
